import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { almacen, newOperationId, type OpeningStockPreview } from '../api';
import { filasDeLaHoja, type FilaDeCarga, type LimitesDeLaFila } from '../cargaInicial';
import { Boton, Cabecera, ConAyuda, Fallo, Filtro, Pastilla, Tarjeta } from '../componentes';
import { descargarCsv } from '../csv';
import { cantidad, soles } from '../formato';
import { leerHoja } from '../hoja';
import { useVocabulario } from '../vocabulario';

/** Por dónde se encontró cada fila, dicho para quien revisa. */
const VIA: Record<NonNullable<OpeningStockPreview['via']>, string> = {
  supply_barcode: 'por su código de barras',
  supply_sku: 'por su código',
  product_barcode: 'por el código del producto',
  product_sku: 'por el código del producto',
  supply_name: 'por su nombre',
  product_name: 'por el nombre del producto',
};

/** Una fila de la hoja con lo que dijo la hoja y lo que dijo el servidor. */
interface Revisada {
  fila: FilaDeCarga;
  vista: OpeningStockPreview | null;
  problemas: string[];
  /** El mínimo y el máximo que trae la hoja, si los trae. */
  limites: LimitesDeLaFila | null;
}

/** Si lo que ya hay en el estante es menos que cero: se vendió sin registrar. */
function esNegativo(stock: string | null | undefined): boolean {
  return stock != null && stock.trim().startsWith('-') && /[1-9]/.test(stock);
}

/**
 * La carga inicial: el stock y el costo con que arranca la tienda, desde una
 * hoja de Excel o un CSV. Docs/15 §2.39.
 *
 * **Dos pasos y no uno.** Primero se ve a qué ficha va cada fila, cuánto entra
 * en su unidad —tres cajas de cien son trescientas tabletas— y qué la frena;
 * nada se escribe hasta pulsar «Cargar». Una bodega trae dos mil filas, y
 * descubrir el código mal escrito después de cargar es corregir a mano en el
 * kardex.
 *
 * **No se escribe el stock encima**: cada fila es una entrada del kardex con su
 * costo (`INVENTARIO-INICIAL`), y se carga todo o nada, una sola vez aunque se
 * pulse dos.
 */
export function CargaInicial() {
  const vocabulario = useVocabulario();
  const clientes = useQueryClient();

  const [archivo, setArchivo] = useState<string | null>(null);
  const [revisadas, setRevisadas] = useState<Revisada[]>([]);
  const [falta, setFalta] = useState<string | null>(null);
  const [leyendo, setLeyendo] = useState(false);
  const [errorAlLeer, setErrorAlLeer] = useState<unknown>(null);
  const [soloProblemas, setSoloProblemas] = useState(false);
  const [cargadas, setCargadas] = useState<number | null>(null);
  const [limitesPuestos, setLimitesPuestos] = useState<{ puestos: number; fallaron: number } | null>(null);

  /*
   * Una operación por hoja revisada: el doble clic en «Cargar», o el
   * reintento tras un corte, manda la misma y el servidor no carga dos veces.
   * Otra hoja es otra operación.
   */
  const [operacion, setOperacion] = useState(newOperationId);

  const listas = useMemo(
    () => revisadas.filter((uno) => uno.problemas.length === 0 && uno.vista !== null),
    [revisadas],
  );
  const conProblemas = revisadas.length - listas.length;
  const visibles = soloProblemas ? revisadas.filter((uno) => uno.problemas.length > 0) : revisadas;

  async function elegir(elegido: File) {
    setArchivo(elegido.name);
    setRevisadas([]);
    setFalta(null);
    setErrorAlLeer(null);
    setCargadas(null);
    setLimitesPuestos(null);
    setOperacion(newOperationId());
    setLeyendo(true);

    try {
      const hoja = filasDeLaHoja(await leerHoja(elegido));
      if (hoja.falta !== null) {
        setFalta(hoja.falta);
        return;
      }
      if (hoja.filas.length > 5000) {
        setFalta('La hoja tiene más de 5 000 filas: pártela en dos y cárgalas una tras otra.');
        return;
      }

      // Solo pregunta al servidor lo que la hoja trae bien escrito.
      const paraPreguntar = hoja.filas.filter((uno) => uno.problemas.length === 0).map((uno) => uno.fila);
      const vistas =
        paraPreguntar.length === 0 ? [] : await almacen.previewOpeningStock(paraPreguntar);
      const porFila = new Map(vistas.map((vista) => [vista.row, vista]));

      setRevisadas(
        hoja.filas.map(({ fila, problemas, limites }) => {
          const vista = porFila.get(fila.row) ?? null;
          return { fila, vista, limites, problemas: [...problemas, ...(vista?.problems ?? [])] };
        }),
      );
    } catch (error) {
      setErrorAlLeer(error);
    } finally {
      setLeyendo(false);
    }
  }

  /**
   * Los mínimos y máximos de la hoja, ficha por ficha, después de cargar.
   *
   * Después y no antes: lo que se une al almacén al cargar todavía no tiene
   * ficha en la vista previa, y se vuelve a preguntar dónde quedó. Uno que
   * falle no deshace la carga —el stock ya entró y es lo que importa—: se dice
   * cuántos no se pudieron poner.
   */
  async function ponerLimites(cargadasConLimites: Revisada[]): Promise<void> {
    if (cargadasConLimites.length === 0) return;

    const sinFicha = cargadasConLimites.filter((uno) => uno.vista?.supplyId == null).map((uno) => uno.fila);
    const ahora = sinFicha.length === 0 ? [] : await almacen.previewOpeningStock(sinFicha).catch(() => []);
    const fichaDe = new Map(ahora.map((vista) => [vista.row, vista.supplyId]));

    let puestos = 0;
    let fallaron = 0;
    for (const uno of cargadasConLimites) {
      const supplyId = uno.vista?.supplyId ?? fichaDe.get(uno.fila.row) ?? null;
      if (supplyId === null || uno.limites === null) {
        fallaron += 1;
        continue;
      }
      try {
        await almacen.setLimits(supplyId, uno.limites);
        puestos += 1;
      } catch {
        fallaron += 1;
      }
    }
    setLimitesPuestos({ puestos, fallaron });
  }

  const cargar = useMutation({
    mutationFn: () =>
      almacen.importOpeningStock({ operationId: operacion, rows: listas.map((uno) => uno.fila) }),
    onSuccess: async (resultado) => {
      setCargadas(resultado.imported);
      await ponerLimites(listas.filter((uno) => uno.limites !== null));
      setRevisadas([]);
      // Lo que enseñan las demás pantallas cambió: el stock, el kardex, lo que vale.
      void clientes.invalidateQueries();
    },
  });

  return (
    <>
      <Cabecera
        titulo="Carga inicial"
        subtitulo={`El stock y el costo con que arranca el ${vocabulario.negocio}, desde una hoja`}
        ayuda="Para empezar a usar el almacén sin teclear lo que ya hay: una hoja de Excel o un CSV con el código de barras o el nombre, la cantidad y el costo de cada uno. Cada fila entra al kardex como inventario inicial."
        acciones={
          <Boton
            onClick={() =>
              descargarCsv(
                'carga-inicial.csv',
                ['Código de barras', 'Nombre', 'Cantidad', 'Costo', 'Lote', 'Vencimiento', 'Mínimo', 'Máximo'],
                [['7750182000123', 'Inca Kola 500 ml', '24', '1,80', '', '', '12', '48']],
              )
            }
          >
            Descargar la plantilla
          </Boton>
        }
      />

      <div className="cuerpo">
        <Tarjeta>
          <p className="nota">
            Una fila por {vocabulario.insumo}, con su <strong>código de barras</strong> (o el de su
            ficha, o su nombre si no tiene), la <strong>cantidad</strong> que hay hoy y su{' '}
            <strong>costo sin IGV</strong> por unidad. Si va por lote, el lote y su vencimiento
            (31/03/2027). Una caja o un pack con su propio código entra en la unidad del almacén:
            tres cajas de cien son trescientas. Si pones las columnas <strong>Mínimo</strong> y{' '}
            <strong>Máximo</strong>, quedan puestas en cada ficha: sin mínimo, el almacén no avisa
            cuándo pedir.
          </p>
          <div className="campo">
            <label htmlFor="hoja-de-carga">La hoja (.xlsx o .csv)</label>
            <input
              id="hoja-de-carga"
              type="file"
              accept=".xlsx,.csv,.txt"
              disabled={leyendo || cargar.isPending}
              onChange={(evento) => {
                const elegido = evento.target.files?.[0];
                evento.target.value = '';
                if (elegido !== undefined) void elegir(elegido);
              }}
            />
          </div>

          {leyendo ? <p className="cargando">Revisando {archivo}…</p> : null}
          {falta !== null ? <p className="aviso-caja aviso-caja--peligro">{falta}</p> : null}
          {errorAlLeer !== null ? <Fallo error={errorAlLeer} /> : null}
          {cargar.isError ? <Fallo error={cargar.error} onReintentar={() => cargar.mutate()} /> : null}
          {cargadas !== null ? (
            <p className="aviso-caja">
              Se cargaron {cargadas} {cargadas === 1 ? 'fila' : 'filas'} de {archivo}. Están en el
              kardex como inventario inicial.
              {limitesPuestos === null || limitesPuestos.puestos + limitesPuestos.fallaron === 0
                ? ''
                : ` Se pusieron ${limitesPuestos.puestos} mínimos y máximos${
                    limitesPuestos.fallaron > 0
                      ? `; ${limitesPuestos.fallaron} no se pudieron poner: ponlos desde la ficha`
                      : ''
                  }.`}
            </p>
          ) : null}
        </Tarjeta>

        {revisadas.length > 0 ? (
          <Tarjeta>
            <div className="tarjeta__cabeza">
              <h2 className="tarjeta__titulo">{archivo}</h2>
              <Boton
                variante="primario"
                disabled={listas.length === 0 || cargar.isPending}
                onClick={() => cargar.mutate()}
              >
                {cargar.isPending
                  ? 'Cargando…'
                  : `Cargar ${listas.length} ${listas.length === 1 ? 'fila' : 'filas'}`}
              </Boton>
            </div>

            <div className="filtros">
              <Filtro texto="Todas" cuenta={revisadas.length} activo={!soloProblemas} onClick={() => setSoloProblemas(false)} />
              <Filtro texto="Con problemas" cuenta={conProblemas} activo={soloProblemas} onClick={() => setSoloProblemas(true)} />
            </div>

            {conProblemas > 0 ? (
              <p className="nota">
                Las filas con problemas no se cargan. Corrígelas en la hoja y vuelve a elegirla:
                las que ya estaban bien se revisan otra vez, y nada entra dos veces mientras no
                pulses «Cargar».
              </p>
            ) : null}

            <div className="tabla-envoltura">
              <table>
                <thead>
                  <tr>
                    <th className="numero">Fila</th>
                    <th>En la hoja</th>
                    <th>
                      <ConAyuda ayuda="La ficha del almacén a la que entra, y cómo se encontró.">Va a</ConAyuda>
                    </th>
                    <th className="numero">
                      <ConAyuda ayuda="Lo que entra, en la unidad del almacén.">Entra</ConAyuda>
                    </th>
                    <th className="numero">
                      <ConAyuda ayuda="Por unidad del almacén, sin IGV.">Costo</ConAyuda>
                    </th>
                    <th className="numero">
                      <ConAyuda ayuda="Lo que ya hay en el estante de este local: lo cargado se suma. Si figura en negativo —se vendió sin registrarlo—, lo que cargues será lo que hay.">Ya hay</ConAyuda>
                    </th>
                    <th className="numero">
                      <ConAyuda ayuda="El mínimo y el máximo que trae la hoja: quedan puestos en la ficha. Vacío, no se cambia.">Mín · máx</ConAyuda>
                    </th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {visibles.map(({ fila, vista, problemas, limites }) => (
                    <tr key={fila.row}>
                      <td className="numero mono apagado">{fila.row}</td>
                      <td>
                        <div className="celda-nombre__texto">
                          <strong>{fila.name ?? fila.code}</strong>
                          {fila.name !== null && fila.code !== null ? (
                            <span className="mono apagado">{fila.code}</span>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        {vista?.supplyName == null ? (
                          <span className="apagado">—</span>
                        ) : (
                          <div className="celda-nombre__texto">
                            <strong>{vista.supplyName}</strong>
                            <span className="apagado">
                              {vista.willLinkProduct
                                ? 'se une al almacén al cargar'
                                : vista.via === null
                                  ? ''
                                  : VIA[vista.via]}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="numero mono">
                        {vista?.quantity == null
                          ? fila.quantity
                          : `${cantidad(vista.quantity)} ${vista.unitCode ?? ''}`}
                        {vista?.unitsPerRow == null ? null : (
                          <span className="apagado"> · {fila.quantity} × {vista.unitsPerRow}</span>
                        )}
                      </td>
                      <td className="numero mono">
                        {vista?.unitCost == null ? fila.unitCost : soles(vista.unitCost)}
                      </td>
                      <td className="numero mono apagado">
                        {vista?.currentStock == null ? '' : cantidad(vista.currentStock)}
                        {esNegativo(vista?.currentStock) ? (
                          /*
                           * En negativo no se suma: sumarle cien a -20 dejaba 80
                           * cuando en el estante se contaron cien. Lo que se carga
                           * pasa a ser lo que hay.
                           */
                          <span className="negativo">
                            {' '}
                            · hoy figura {cantidad(vista!.currentStock!)}: lo que cargues será lo que hay
                          </span>
                        ) : null}
                      </td>
                      <td className="numero mono apagado">
                        {limites === null
                          ? ''
                          : `${limites.minimum === null ? '—' : cantidad(limites.minimum)} · ${
                              limites.maximum === null ? '—' : cantidad(limites.maximum)
                            }`}
                      </td>
                      <td>
                        {problemas.length === 0 ? (
                          <Pastilla texto="Lista" tono="exito" />
                        ) : (
                          <span className="negativo">{problemas.join(' ')}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Tarjeta>
        ) : null}
      </div>
    </>
  );
}
